/**
 * 絵コンテモードの「モーションで上書き」(モーションカード・境界調整モーダル) の純関数。
 * docs/features/brand-cut-motion-graphics-plan.md §10.13 3. / 4. / 5. / 8.・§10.13.1。
 *
 * 画面の出し分け (2 択の表示条件・生成の無効化理由・鮮度の文言・吸着候補・コマ送り) だけを持つ。
 * 区間の算出と上書きの鮮度の正本は backend (`GET .../storyboard/spans`) で、ここはその応答を読むだけ。
 */
import type {
  StoryboardMotionOverlayRow,
  StoryboardSpanRow,
  StoryboardSpansCard,
  StoryboardSpansResponse,
} from '../../../../features/scenarios/api';
import type { MaterialSceneCut } from '../../../../features/materials/api';
import {
  MOTION_MIN_DURATION_SEC,
  framesToSec,
  secToFrames,
  type VideoFps,
} from '../../../../features/scenarios/storyboardSpans';

/** 2 択の値 (`scenes.video_input_mode`): null = 絵コンテ動画のまま / 'motion' = モーションで上書き。 */
export type StoryboardSceneMode = 'storyboard' | 'motion';

/** 2 択のラベル。 */
export const STORYBOARD_SCENE_MODE_LABELS: Record<StoryboardSceneMode, string> = {
  storyboard: '絵コンテ動画のまま',
  motion: 'モーションで上書き',
};

/**
 * 絵コンテの本編シーンが「モーションで上書き」の対象か (3 点メニューに「モーションで上書き」を出す条件)。
 * ブランドカット (タイトル / エンドカード) は対象外 (§10.13 2.「ブランドカットは対象外」)。
 */
export function showStoryboardMotionSwitch(
  videoGenerationMode: string | null | undefined,
  scene: { brand_cut_position?: 'start' | 'end' | null },
): boolean {
  return videoGenerationMode === 'storyboard' && !scene.brand_cut_position;
}

/** 絵コンテの本編シーンにモーションカードを出すか = 対象シーンで、かつ「モーションで上書き」が選ばれている
 *  (3 点メニューで選ぶか、シナリオ生成時に AI がモーション向きと判定して初期値 'motion' が入っている)。 */
export function showStoryboardMotionCard(
  videoGenerationMode: string | null | undefined,
  scene: { brand_cut_position?: 'start' | 'end' | null; video_input_mode?: string | null },
): boolean {
  return showStoryboardMotionSwitch(videoGenerationMode, scene) && storyboardSceneMode(scene) === 'motion';
}

/** 絵コンテでの明示値 → 2 択の表示値 (motion 以外の明示値は絵コンテでは不可なので「絵コンテ動画のまま」に倒す)。 */
export function storyboardSceneMode(scene: { video_input_mode?: string | null }): StoryboardSceneMode {
  return scene.video_input_mode === 'motion' ? 'motion' : 'storyboard';
}

/** 2 択の値 → `video_input_mode` の PATCH 本文 (motion ↔ null)。 */
export function storyboardSceneModePatch(mode: StoryboardSceneMode): { video_input_mode: 'motion' | null } {
  return { video_input_mode: mode === 'motion' ? 'motion' : null };
}

// ── spans 応答からこのシーンの区間と上書きを引く ───────────────────────────

export interface SceneSpanLookup {
  card: StoryboardSpansCard;
  /** 担当カードの応答内の添字 (0 始まり)。 */
  cardIndex: number;
  span: StoryboardSpanRow;
  /** 同じカードの区間列の中での添字 (隣接シーンの特定に使う)。 */
  spanIndex: number;
  /** 「利用しない」(disabled) のときは応答に含まれないので null。 */
  overlay: StoryboardMotionOverlayRow | null;
}

/** spans 応答からシーンの区間を引く。担当カードの動画が無い (区間が決まっていない) なら null。 */
export function findSceneSpan(response: StoryboardSpansResponse | null | undefined, sceneId: string): SceneSpanLookup | null {
  if (!response) return null;
  for (let cardIndex = 0; cardIndex < response.cards.length; cardIndex += 1) {
    const card = response.cards[cardIndex]!;
    const spanIndex = card.spans.findIndex((s) => s.scene_id === sceneId);
    if (spanIndex < 0) continue;
    return {
      card,
      cardIndex,
      span: card.spans[spanIndex]!,
      spanIndex,
      overlay: card.motion_overlays.find((o) => o.scene_id === sceneId) ?? null,
    };
  }
  return null;
}

/**
 * 「動画N」の番号: 画面の動画カード列 (WorkPanel) と同じ規則で、本編の先頭シーンと動画を持つ本編シーンを
 * order 順にカードと見なし、担当カードが何枚目かを返す。見つからなければ null。
 * (「動画を追加」でローカルに足しただけの未生成カードは数えられないので、そのときは 1 つずれる)
 */
export function storyboardCardNumber(
  scenes: readonly { id: string; order: number; brand_cut_position?: 'start' | 'end' | null; video_material_id?: string | null }[],
  cardSceneId: string,
  localVideoMaterialIds: Record<string, string> = {},
  scenarioId = '',
): number | null {
  const body = [...scenes]
    .filter((s) => s.brand_cut_position !== 'start' && s.brand_cut_position !== 'end')
    .sort((a, b) => a.order - b.order);
  if (body.length === 0) return null;
  const anchors = body.filter((s, i) => (
    i === 0 || !!s.video_material_id || !!localVideoMaterialIds[`${scenarioId}-scene-${s.order}`]
  ));
  const idx = anchors.findIndex((s) => s.id === cardSceneId);
  return idx >= 0 ? idx + 1 : null;
}

// ── 表示 ───────────────────────────────────────────────────────────────────

/** 秒を 2 桁で (出口の丸めは表示だけ)。 */
export function formatSec(sec: number): string {
  return `${(Math.round(sec * 100) / 100).toFixed(2)}秒`;
}

/** 秒を「m:ss.ff」(例 8.25 → 0:08.25・65.5 → 1:05.50) にする。シーンカードの開始・終了 (useSceneTimeline.formatTimecode の m:ss) に小数 2 桁を足した形。 */
export function formatSpanTimecode(sec: number): string {
  const total = Math.max(0, Math.round(sec * 100) / 100);
  const m = Math.floor(total / 60);
  const s = total - m * 60;
  return `${m}:${s.toFixed(2).padStart(5, '0')}`;
}

/**
 * 区間の表示 (2026-10-14 利用者指示): 「動画1」(担当カード。背景色付きのチップ)・「0:08.25-0:12.04」(開始-終了。シーンカードの
 * 開始・終了秒と同じタイムコードの書体)・「3.79秒」(長さ) の 3 つ。カードは `動画1 0:08.25-0:12.04・3.79秒` の並びで出す。
 */
export function spanLabelParts(
  cardNumber: number | null,
  span: { in_sec: number; out_sec: number },
): { card: string | null; range: string; duration: string } {
  return {
    card: cardNumber != null ? `動画${cardNumber}` : null,
    range: `${formatSpanTimecode(span.in_sec)}-${formatSpanTimecode(span.out_sec)}`,
    duration: formatSec(span.out_sec - span.in_sec),
  };
}

/** 区間の表示の文字列版「動画1 0:08.25-0:12.04・3.79秒」(テスト・ログ用)。 */
export function spanLabel(cardNumber: number | null, span: { in_sec: number; out_sec: number }): string {
  const parts = spanLabelParts(cardNumber, span);
  return `${parts.card ? `${parts.card} ` : ''}${parts.range}・${parts.duration}`;
}

/** 区間の長さ (秒)。 */
export function spanDurationSec(span: { in_sec: number; out_sec: number }): number {
  return Math.max(0, span.out_sec - span.in_sec);
}

export interface MotionGenerateGateInput {
  /** 担当カードの動画があり区間が決まっている。 */
  hasSpan: boolean;
  /** 区間の長さ (秒)。hasSpan=false なら無視。 */
  spanSec: number;
  /** 定額の料金行 (credit_rate_config) が未設定 (0)。 */
  rateMissing: boolean;
  /** 生成中。 */
  loading: boolean;
}

/** 生成ボタンを無効化する理由 (null = 生成できる)。優先順: 生成中 → 料金未設定 → 区間なし → 1 秒未満。 */
export function motionGenerateDisabledReason(input: MotionGenerateGateInput): string | null {
  if (input.loading) return 'モーション生成中…';
  if (input.rateMissing) return 'モーショングラフィックスの料金が設定されていないため、現在ご利用いただけません';
  if (!input.hasSpan) return '絵コンテ動画を生成すると区間が決まります';
  if (input.spanSec < MOTION_MIN_DURATION_SEC) {
    return '区間が1秒未満のためモーションを生成できません。境界を広げてください';
  }
  return null;
}

/**
 * 上書きの鮮度の注記 (§10.13 8. / §10.13.1 契約 C)。
 * - stale (元動画が変わった / 尺が 0.3 秒超ずれた) → 再生成を促す
 * - trim / freeze (0.3 秒以内のずれ) → 控えめな注記
 * - exact → null
 */
export function motionOverlayFreshnessNotice(
  overlay: Pick<StoryboardMotionOverlayRow, 'fit' | 'reason'> | null | undefined,
): { tone: 'stale' | 'adjust'; text: string } | null {
  if (!overlay) return null;
  if (overlay.fit === 'stale') {
    return { tone: 'stale', text: '絵コンテ動画が変わりました。境界を確認して再生成してください' };
  }
  if (overlay.fit === 'trim') {
    return { tone: 'adjust', text: '区間と生成した尺が少し違うため末尾を切り詰めて合わせます' };
  }
  if (overlay.fit === 'freeze') {
    return { tone: 'adjust', text: '区間と生成した尺が少し違うため末尾で静止して合わせます' };
  }
  return null;
}

/** 境界の補正が版違いで無視された (span_override の 3 条件が揃わない) ときの注記。 */
export function boundaryStaleNotice(span: Pick<StoryboardSpanRow, 'boundary_stale'> | null | undefined): string | null {
  return span?.boundary_stale ? '境界の補正が古くなったため既定の位置に戻しました' : null;
}

// ── 境界調整モーダル ───────────────────────────────────────────────────────

/** 境界 1 つぶんの可動範囲 (フレーム)。locked = 先頭の in (常に 0) / 末尾の out (常に動画の終わり)。 */
export interface BoundaryLimit {
  min: number;
  max: number;
  locked: boolean;
}

export interface BoundaryLimits {
  inLimit: BoundaryLimit;
  outLimit: BoundaryLimit;
  /** 開始境界を共有する前のシーン (無ければ null)。 */
  prevSceneId: string | null;
  /** 終了境界を共有する次のシーン (無ければ null)。 */
  nextSceneId: string | null;
}

/**
 * 対象シーンの in / out の可動範囲。
 * - in: 前のシーンの in + 1 以上 (前のシーンを 1 コマ以上残す)・自分の out − 1 以下
 * - out: 自分の in + 1 以上・次のシーンの out − 1 以下 (次のシーンを 1 コマ以上残す)
 * - 先頭シーンの in は 0 固定・末尾シーンの out は segment_frames 固定 (BE resolve と同じ)
 * 自分の in / out の相互制約 (in < out) は `clampBoundary` が現在値で当てる。
 */
export function boundaryLimits(card: Pick<StoryboardSpansCard, 'spans' | 'segment_frames'>, spanIndex: number): BoundaryLimits {
  const spans = card.spans;
  const prev = spanIndex > 0 ? spans[spanIndex - 1]! : null;
  const next = spanIndex < spans.length - 1 ? spans[spanIndex + 1]! : null;
  return {
    inLimit: prev
      ? { min: prev.in_frame + 1, max: Math.max(prev.in_frame + 1, card.segment_frames - 1), locked: false }
      : { min: 0, max: 0, locked: true },
    outLimit: next
      ? { min: 1, max: Math.max(1, next.out_frame - 1), locked: false }
      : { min: card.segment_frames, max: card.segment_frames, locked: true },
    prevSceneId: prev?.scene_id ?? null,
    nextSceneId: next?.scene_id ?? null,
  };
}

/**
 * 境界の候補値を可動範囲と相手の境界で丸める (in なら out − 1 以下・out なら in + 1 以上)。
 * locked なら固定値。
 */
export function clampBoundary(
  which: 'in' | 'out',
  candidate: number,
  current: { inFrame: number; outFrame: number },
  limits: BoundaryLimits,
): number {
  const limit = which === 'in' ? limits.inLimit : limits.outLimit;
  if (limit.locked) return limit.min;
  const v = Math.round(candidate);
  const lo = which === 'in' ? limit.min : Math.max(limit.min, current.inFrame + 1);
  const hi = which === 'in' ? Math.min(limit.max, current.outFrame - 1) : limit.max;
  return Math.max(lo, Math.min(hi, v));
}

/** 0.5 秒ぶんのフレーム数 (最低 1)。 */
export function halfSecondFrames(fps: VideoFps): number {
  return Math.max(1, secToFrames(0.5, fps));
}

/** コマ送り (±1 コマ / ±0.5 秒) の結果 (丸めは clampBoundary)。 */
export function stepBoundary(
  which: 'in' | 'out',
  current: { inFrame: number; outFrame: number },
  step: 'frame' | 'half',
  direction: -1 | 1,
  fps: VideoFps,
  limits: BoundaryLimits,
): number {
  const base = which === 'in' ? current.inFrame : current.outFrame;
  const delta = (step === 'frame' ? 1 : halfSecondFrames(fps)) * direction;
  return clampBoundary(which, base + delta, current, limits);
}

/** 吸着候補を探す窓 (推定位置の前後。秒)。§10.13 3. */
export const CUT_SNAP_WINDOW_SEC = 1;

/**
 * 推定位置 (フレーム) から ±1 秒以内で最も近い検出カット (同じ距離なら先 = 小さいフレーム)。無ければ null。
 * カットの fps と区間の fps が違う場合は秒で比べる。
 */
export function nearestCutCandidate(
  cuts: readonly MaterialSceneCut[],
  atFrame: number,
  fps: VideoFps,
  windowSec: number = CUT_SNAP_WINDOW_SEC,
): MaterialSceneCut | null {
  const atSec = framesToSec(atFrame, fps);
  let best: MaterialSceneCut | null = null;
  let bestDist = Number.POSITIVE_INFINITY;
  for (const cut of cuts) {
    const dist = Math.abs(cut.sec - atSec);
    if (dist > windowSec + 1e-9) continue;
    if (dist < bestDist - 1e-9 || (Math.abs(dist - bestDist) <= 1e-9 && best && cut.sec < best.sec)) {
      best = cut;
      bestDist = dist;
    }
  }
  return best;
}

/** 検出カットを区間の fps のフレーム番号にする (カットが秒で来ても同じ結果)。 */
export function cutToFrame(cut: MaterialSceneCut, fps: VideoFps): number {
  return secToFrames(cut.sec, fps);
}

/** 保存本文: 対象シーンだけを送る (隣接の共有境界は BE が同期して書く)。 */
export function buildBoundariesPayload(
  card: Pick<StoryboardSpansCard, 'card_scene_id'>,
  sceneId: string,
  inFrame: number,
  outFrame: number,
): { card_scene_id: string; boundaries: Array<{ scene_id: string; in_frame: number; out_frame: number }> } {
  return { card_scene_id: card.card_scene_id, boundaries: [{ scene_id: sceneId, in_frame: inFrame, out_frame: outFrame }] };
}

/** 保存前の検証 (BE と同じ条件の FE 側ガード)。null = 保存できる。 */
export function validateBoundaries(
  inFrame: number,
  outFrame: number,
  limits: BoundaryLimits,
): string | null {
  if (!Number.isInteger(inFrame) || !Number.isInteger(outFrame)) return '境界が不正です';
  if (inFrame >= outFrame) return '開始は終了より前にしてください';
  if (inFrame < limits.inLimit.min || inFrame > limits.inLimit.max) return '開始が前のシーンの範囲を超えています';
  if (outFrame < limits.outLimit.min || outFrame > limits.outLimit.max) return '終了が次のシーンの範囲を超えています';
  return null;
}

/** フィルムストリップの読み込み済み範囲に境界が含まれるか (含まれなければ取り直す)。 */
export function stripCoversFrame(frames: readonly { frame: number }[], frame: number): boolean {
  return frames.some((f) => f.frame === frame);
}
